// =============================================================================
// features/assistant/hooks/useEditableFocus.ts — "O usuário está digitando?"
// (F51-S37).
//
// A aba lateral do copiloto some enquanto um campo editável está em foco ou o
// teclado virtual está aberto: com o teclado em cima, a aba disputaria a pouca
// tela que sobra para o campo. A decisão em si é pura (`edgeTab.ts`); aqui só
// ligamos `focusin`/`focusout` e o `visualViewport`.
// =============================================================================

import * as React from 'react';

import { isEditableElement, isVirtualKeyboardOpen } from '../edgeTab';

/**
 * Tolerância ao sair de um campo: na troca de foco entre dois campos o
 * `focusout` do primeiro chega antes do `focusin` do segundo — sem a espera a
 * aba piscaria entre eles.
 */
const BLUR_GRACE_MS = 120;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return isEditableElement({
    tagName: target.tagName,
    type: target instanceof HTMLInputElement ? target.type : target.getAttribute('type'),
    isContentEditable: target.isContentEditable,
    role: target.getAttribute('role'),
  });
}

function readKeyboardOpen(): boolean {
  const viewport = window.visualViewport;
  // Sem visualViewport (navegador antigo): fica só o sinal de foco.
  if (!viewport) return false;
  return isVirtualKeyboardOpen({
    layoutHeight: window.innerHeight,
    visualHeight: viewport.height,
    scale: viewport.scale,
  });
}

export interface EditableFocusState {
  editableFocused: boolean;
  keyboardOpen: boolean;
}

export function useEditableFocus(): EditableFocusState {
  const [editableFocused, setEditableFocused] = React.useState(() =>
    isEditableTarget(document.activeElement),
  );
  const [keyboardOpen, setKeyboardOpen] = React.useState(readKeyboardOpen);

  React.useEffect(() => {
    let blurTimer: number | undefined;

    const onFocusIn = (event: FocusEvent): void => {
      window.clearTimeout(blurTimer);
      // Esconde na hora ao entrar num campo; reaparece só depois da tolerância.
      setEditableFocused(isEditableTarget(event.target));
    };
    const onFocusOut = (): void => {
      window.clearTimeout(blurTimer);
      blurTimer = window.setTimeout(() => {
        setEditableFocused(isEditableTarget(document.activeElement));
      }, BLUR_GRACE_MS);
    };

    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      window.clearTimeout(blurTimer);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  React.useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const onViewportChange = (): void => setKeyboardOpen(readKeyboardOpen());
    viewport.addEventListener('resize', onViewportChange);
    window.addEventListener('resize', onViewportChange);
    return () => {
      viewport.removeEventListener('resize', onViewportChange);
      window.removeEventListener('resize', onViewportChange);
    };
  }, []);

  return { editableFocused, keyboardOpen };
}
